import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Bank, UploadSimple, ImageSquare } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { API, formatApiError } from "../lib/api";

export default function RekeningYayasan() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ bank_name: "", account_number: "", holder_name: "", qris_image_path: "", is_active: true, sort_order: 0, notes: "" });
  const fileRef = useRef();

  const load = async () => {
    try { const { data } = await api.get("/rekening"); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ bank_name: "", account_number: "", holder_name: "", qris_image_path: "", is_active: true, sort_order: items.length, notes: "" });
    setOpen(true);
  };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setOpen(true); };

  const save = async () => {
    if (!form.bank_name || !form.account_number || !form.holder_name) { toast.error("Bank / No. Rekening / Atas Nama wajib diisi"); return; }
    try {
      if (editing) await api.put(`/rekening/${editing.id}`, form);
      else await api.post("/rekening", form);
      toast.success(editing ? "Rekening diperbarui" : "Rekening ditambahkan");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!confirm("Hapus rekening ini?")) return;
    try { await api.delete(`/rekening/${id}`); toast.success("Terhapus"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadQris = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "qris");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, qris_image_path: data.path }));
      toast.success("QRIS terunggah");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal unggah"); }
  };

  return (
    <div data-testid="rekening-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Pengaturan</div>
          <h1 className="font-display text-3xl font-black text-gray-900">Rekening Yayasan</h1>
          <p className="text-gray-600 text-sm mt-1">Daftar rekening tujuan pembayaran wali murid — tampil di portal wali saat mengajukan pembayaran.</p>
        </div>
        <Button onClick={openNew} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="rekening-add-btn"><Plus size={16} className="mr-1.5" />Tambah Rekening</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((r) => (
          <div key={r.id} className={`bg-white border-2 rounded-2xl p-5 relative ${r.is_active ? "border-[#0B8D95]/30" : "border-gray-200 opacity-60"}`} data-testid={`rekening-card-${r.id}`}>
            {!r.is_active && <div className="absolute top-3 right-3 text-[10px] uppercase font-bold px-2 py-0.5 bg-slate-200 text-slate-600 rounded">Nonaktif</div>}
            <div className="flex items-start gap-3 mb-3">
              <div className="h-10 w-10 rounded-lg bg-[#0B8D95]/10 flex items-center justify-center flex-shrink-0"><Bank size={20} weight="duotone" className="text-[#0B8D95]" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Bank</div>
                <div className="font-display font-black text-lg">{r.bank_name}</div>
              </div>
            </div>
            <div className="space-y-2 mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">No. Rekening</div>
                <div className="font-mono font-bold text-xl tabular-nums select-all">{r.account_number}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Atas Nama</div>
                <div className="text-sm font-semibold">{r.holder_name}</div>
              </div>
              {r.qris_image_path && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1">QRIS</div>
                  <img src={`${API}/files/${r.qris_image_path}`} alt="QRIS" className="h-24 w-24 object-contain border border-gray-200 rounded" />
                </div>
              )}
              {r.notes && <div className="text-xs text-gray-500 italic">{r.notes}</div>}
            </div>
            <div className="flex gap-2 pt-3 border-t border-gray-100">
              <Button variant="outline" size="sm" onClick={() => openEdit(r)} className="flex-1" data-testid={`rekening-edit-${r.id}`}><PencilSimple size={14} className="mr-1.5" />Edit</Button>
              <Button variant="outline" size="sm" onClick={() => remove(r.id)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`rekening-delete-${r.id}`}><Trash size={14} /></Button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="col-span-full bg-white border border-dashed border-gray-300 rounded-xl p-12 text-center">
            <Bank size={40} weight="duotone" className="mx-auto text-gray-300 mb-2" />
            <div className="font-semibold text-gray-700">Belum ada rekening</div>
            <div className="text-xs text-gray-500 mt-1">Tambahkan rekening bank yayasan supaya wali murid tahu tujuan transfer.</div>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="rekening-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Rekening" : "Tambah Rekening"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nama Bank *</Label><Input value={form.bank_name || ""} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="BSI, BCA, BRI..." data-testid="rekening-bank-input" /></div>
              <div><Label>Urutan</Label><Input type="number" value={form.sort_order || 0} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} /></div>
            </div>
            <div><Label>No. Rekening *</Label><Input value={form.account_number || ""} onChange={(e) => setForm({ ...form, account_number: e.target.value })} placeholder="7123456789" data-testid="rekening-nomor-input" /></div>
            <div><Label>Atas Nama *</Label><Input value={form.holder_name || ""} onChange={(e) => setForm({ ...form, holder_name: e.target.value })} placeholder="Yayasan Al Uswah" data-testid="rekening-nama-input" /></div>
            <div><Label>Catatan (opsional)</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="misal: khusus SPP bulanan" /></div>
            <div>
              <Label>QRIS (opsional, gambar)</Label>
              <div className="flex items-center gap-3 mt-1">
                {form.qris_image_path ? (
                  <img src={`${API}/files/${form.qris_image_path}`} alt="QRIS" className="h-20 w-20 object-contain border border-gray-200 rounded" />
                ) : (
                  <div className="h-20 w-20 bg-gray-50 border border-dashed border-gray-300 rounded flex items-center justify-center"><ImageSquare size={24} className="text-gray-300" /></div>
                )}
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadQris(e.target.files[0])} />
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} type="button"><UploadSimple size={14} className="mr-1.5" />{form.qris_image_path ? "Ganti" : "Unggah"}</Button>
                {form.qris_image_path && <Button variant="outline" size="sm" onClick={() => setForm({ ...form, qris_image_path: "" })} type="button" className="text-red-600">Hapus</Button>}
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <input type="checkbox" id="rek_active" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="rekening-active-input" />
              <Label htmlFor="rek_active" className="cursor-pointer">Aktif (tampil di portal wali)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="rekening-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
